import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Container } from '@/components/layout/Container';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { isAuthConfigured } from '@/config';
import { zodResolver } from '@/utils/zodResolver';
import { useAuth } from '../AuthProvider';
import { LOCAL_AUTH, LOCAL_IDENTITIES } from '../localAuth';
import { describeAuthError } from '../cognito';

const schema = z.object({
  email: z.string().trim().email('Enter a valid email address'),
  password: z.string().min(1, 'Enter your password'),
});
type Values = z.infer<typeof schema>;

export function LoginPage() {
  const { signIn, signInLocal, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const { register, handleSubmit, formState: { errors, isSubmitting } } =
    useForm<Values>({ resolver: zodResolver<Values>(schema) });

  if (status === 'authenticated') return <Navigate to={from} replace />;

  const onSubmit = handleSubmit(async ({ email, password }) => {
    setError(null);
    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (e) {
      setError(describeAuthError(e));
    }
  });

  return (
    <Container className="max-w-md">
      <PageHeader title="Log in" description="Welcome back. Sign in to book or offer a ride." />
      <Card>
        <CardBody>
          {LOCAL_AUTH && (
            <div className="mb-4 space-y-2" aria-label="Local development sign-in">
              <Alert tone="info" title="Local development">Pick an identity. This is only available in dev builds.</Alert>
              {LOCAL_IDENTITIES.map((id) => (
                <Button key={id.userId} variant="secondary" className="w-full" onClick={() => { signInLocal(id); navigate(from, { replace: true }); }}>
                  {id.label}
                </Button>
              ))}
            </div>
          )}
          {!isAuthConfigured() && !LOCAL_AUTH && (
            <div className="mb-4"><Alert tone="warning" title="Sign-in not configured">
              Set the Cognito environment variables (see .env.example) to enable sign-in.
            </Alert></div>
          )}
          <form onSubmit={onSubmit} noValidate className="space-y-4" aria-label="Log in">
            {error && <Alert tone="error">{error}</Alert>}
            <Field label="Email" type="email" autoComplete="email" error={errors.email?.message} {...register('email')} />
            <Field label="Password" type="password" autoComplete="current-password" error={errors.password?.message} {...register('password')} />
            <Button type="submit" size="lg" className="w-full" loading={isSubmitting}>Log in</Button>
          </form>
          <p className="mt-4 flex justify-between text-sm">
            <Link className="text-brand-700 hover:underline" to="/forgot-password">Forgot password?</Link>
            <Link className="text-brand-700 hover:underline" to="/register">Create an account</Link>
          </p>
        </CardBody>
      </Card>
    </Container>
  );
}
